import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Theme, useMediaQuery } from '@mui/material';
import classnames from 'classnames';
import {
  AINotesMenu,
  NotesDialog,
  NotificationSettings,
  PatientQueue,
  VideoPreviewWithControls,
  WelcomeDialog,
} from 'components';
import { MyWaitingRoom } from './components';
import { IFirstLoginTooltipsContext, ISignalrConnectionContext } from 'context';
import {
  useDialog,
  useFirstLoginTooltips,
  useOpenChatOnNewMessage,
  useSignalrConnection,
  useSubscription,
  useVideo,
} from 'hooks';
import { ApiUser, ApiUserTooltipStagePassedEnum, SubscriptionStatusEnum } from 'types';
import { useAppDispatch, useAppSelector } from 'store';
import { getRoomHistory } from 'store/call';
import { selectUser } from 'store/user';
import { parseIdentity } from 'utils';
import { useTranslation } from 'react-i18next';

import styles from './ProviderRoomPage.module.scss';

export function ProviderRoomPage() {
  // will be moved to Chat component
  useOpenChatOnNewMessage();

  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));
  const isTablet = useMediaQuery((theme: Theme) => theme.breakpoints.between('sm', 'md'));
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const dispatch = useAppDispatch();
  const user: ApiUser | null = useAppSelector(selectUser);
  const subscription = useSubscription();
  const { t } = useTranslation('providerRoom');
  const [isEditWaitingRoomDirty, setIsEditWaitingRoomDirty] = useState(false);
  const providerRoomContainerRef = useRef<HTMLDivElement | null>(null);

  const {
    signalrConnectionId,
    events,
    getPatientsList,
    addEventListener,
    removeEventListener,
  }: ISignalrConnectionContext = useSignalrConnection();
  const { showDialog } = useDialog();
  const { room } = useVideo();

  const { tooltipsState, accept }: IFirstLoginTooltipsContext = useFirstLoginTooltips();

  const shouldShowWelcomeDialog = useMemo(() => {
    return user?.tooltipStagePassed === ApiUserTooltipStagePassedEnum.None && !tooltipsState;
  }, [user, tooltipsState]);

  const shouldShowTrialWelcomeDialog = useMemo(() => {
    return shouldShowWelcomeDialog && subscription?.subscriptionStatus === SubscriptionStatusEnum.Trial;
  }, [subscription, shouldShowWelcomeDialog]);

  const showWelcomeDialog = () => {
    const modal = showDialog(WelcomeDialog, {
      onConfirm: () => {
        modal.destroy();
        accept();
      },
    });
  };

  const confirmProviderRoomNavigation = useCallback((): boolean => {
    if (!isEditWaitingRoomDirty) {
      return true;
    }

    if (!window.confirm(t('unsavedChangesPrompt'))) {
      return false;
    }

    setIsEditWaitingRoomDirty(false);
    return true;
  }, [isEditWaitingRoomDirty, t]);

  useEffect(() => {
    if (shouldShowTrialWelcomeDialog) {
      showWelcomeDialog();
    } else if (shouldShowWelcomeDialog) {
      accept();
    }
  }, [user, subscription]);

  useEffect(() => {
    if (room) {
      return () => {
        room?.disconnect();
      };
    }
  }, [room]);

  useEffect(() => {
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 500);
  }, []);

  useEffect(() => {
    if (signalrConnectionId) {
      getPatientsList();
    }
  }, [signalrConnectionId]);

  useEffect(() => {
    events.on('updatedStatusDataSent', getPatientsList);
  }, [events]);

  const onCallEnded = useCallback(() => {
    const shouldHideNotesDialog = localStorage.getItem('hideNotesDialog') === 'true';
    const participantNamesOverride = Array.from(room?.participants.values() ?? [])
      .map((participant) => {
        const [fallbackIdentity, displayName] = parseIdentity(participant.identity ?? '');
        return (displayName || fallbackIdentity || '').trim();
      })
      .filter(Boolean);

    if (!shouldHideNotesDialog) {
      dispatch(getRoomHistory()).then(() => {
        const modal = showDialog(NotesDialog, {
          participantNamesOverride,
          onConfirm: () => {
            modal.destroy();
          },
        });
      });
    }
  }, [dispatch, room, showDialog]);

  useEffect(() => {
    addEventListener('callEnded', onCallEnded);

    return () => {
      removeEventListener('callEnded', onCallEnded);
    };
  }, [onCallEnded]);

  useEffect(() => {
    const updateProviderRoomLeft = () => {
      const left = providerRoomContainerRef.current?.getBoundingClientRect().left ?? 0;
      document.documentElement.style.setProperty('--app-provider-room-left', `${Math.max(0, left)}px`);
    };

    updateProviderRoomLeft();

    const resizeObserver = new ResizeObserver(() => {
      updateProviderRoomLeft();
    });

    if (providerRoomContainerRef.current) {
      resizeObserver.observe(providerRoomContainerRef.current);
    }

    window.addEventListener('resize', updateProviderRoomLeft);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateProviderRoomLeft);
      document.documentElement.style.removeProperty('--app-provider-room-left');
    };
  }, []);

  return (
    <Box
      ref={providerRoomContainerRef}
      className={classnames(styles.container, {
        [styles.containerRoomNameNull]: !user?.clinicUser.roomName,
      })}
    >
      {isDesktop && (
        <>
          <PatientQueue
            className={styles.patientQueue}
            additionalMenuSlot={
              <AINotesMenu
                aiCreditsCount={user?.clinicUser.aiCreditsCount}
                confirmNavigation={confirmProviderRoomNavigation}
              />
            }
          />
          <MyWaitingRoom className={styles.waitingRoom} onDirtyChange={setIsEditWaitingRoomDirty} />
        </>
      )}

      {isTablet && (
        <>
          {user?.clinicUser.roomName && (
            <>
              <PatientQueue
                className={styles.patientQueue}
                additionalMenuSlot={
                  <AINotesMenu
                    aiCreditsCount={user?.clinicUser.aiCreditsCount}
                    confirmNavigation={confirmProviderRoomNavigation}
                  />
                }
              />
              <VideoPreviewWithControls />
            </>
          )}
          <MyWaitingRoom className={styles.waitingRoom} onDirtyChange={setIsEditWaitingRoomDirty} />
        </>
      )}

      {isMobile && (
        <>
          {user?.clinicUser.roomName && (
            <>
              <MyWaitingRoom className={styles.waitingRoom} onDirtyChange={setIsEditWaitingRoomDirty} />

              <VideoPreviewWithControls />

              <PatientQueue
                className={styles.patientQueue}
                additionalMenuSlot={
                  <AINotesMenu
                    aiCreditsCount={user?.clinicUser.aiCreditsCount}
                    confirmNavigation={confirmProviderRoomNavigation}
                  />
                }
              />

              <div className={styles.notificationSettingsContainer}>
                <NotificationSettings />
              </div>
            </>
          )}

          {!user?.clinicUser.roomName && (
            <MyWaitingRoom className={styles.waitingRoom} onDirtyChange={setIsEditWaitingRoomDirty} />
          )}
        </>
      )}
    </Box>
  );
}
